import React from 'react';
import {observable, action, computed, makeObservable} from 'mobx';
import {observer} from 'mobx-react';
import s from '../../s';
import {Swiper, SwiperSlide} from 'swiper/react';
import 'swiper/css';
import 'swiper/css/scrollbar';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/zoom';
import {Keyboard, Scrollbar, Navigation, Zoom} from 'swiper/modules';
import {r} from '../../rendering/core';
import {prerender_set_pcs} from '../../rendering/web-only-render';
import * as emitter from '../../../global/emitter';
import {screenResizeStore} from '../../../global/store/screen-resize-store';
import SliderTHModal from './slider-th-modal';
import {ProductCustomizationState} from '../../rendering/render.def';
import {ariaPulse, modal, RenderType} from '../../../util';
import {StandardSlide} from './standard-slide';
import {
    THUMBNAIL_SWIPER_BREAKPOINTS,
    THUMB_SWIPER_ID,
    ZOOM_MODAL_ID,
    ZOOM_SWIPER_ID,
    buildProductSlides,
    clearScheduledTimeouts,
    findSlideIndexById,
    getThumbnailSlidesPerView,
    pauseVideosIn,
    scheduleZoomNavigation,
    shouldLoopThumbnails,
    shouldShowThumbnailNavigation,
    type ProductSlidesInput,
} from './product-slides/product-slides';
import {productSlidesInput} from './product-slides/product-slides-input';

@observer
export default class SliderTH extends React.Component {
    @observable zoom_id: string = '';

    private zoomTimers: ReturnType<typeof setTimeout>[] = [];
    private openerEl: HTMLElement | null = null;
    private focusTimer: ReturnType<typeof setTimeout> | null = null;

    constructor(props) {
        super(props);
        this.loadSwiper = this.loadSwiper.bind(this);
        this.loadSwiperZoom = this.loadSwiperZoom.bind(this);
        this.toggleZoom = this.toggleZoom.bind(this);
        makeObservable(this);
    }

    abForceUpdate = async () => this.forceUpdate();

    private onModalClosed = (e: any) => {
        if (e.detail === ZOOM_MODAL_ID) {
            this.closeZoom();
        }
    };

    componentDidMount() {
        emitter.eventEmitter.subscribe(emitter.EventType.AB, 'ab', this.abForceUpdate);
        document.addEventListener('modal-closed', this.onModalClosed);
    }

    componentWillUnmount() {
        emitter.eventEmitter.unsubscribe(emitter.EventType.AB, 'ab');
        document.removeEventListener('modal-closed', this.onModalClosed);
        this.teardownZoom();
        this.clearFocusTimer();
    }

    @computed get show_slider(): boolean {
        if (!s.ready || !s.prod) {
            return false;
        }

        return !(s.prod.MouldingID < 1 && s.prod.Mat1_ID < 1 && s.prod.th1Disabled && s.prod.th2Disabled && s.prod.th3Disabled && s.prod.th4Disabled && s.prod.th5Disabled && s.prod.th6Disabled && s.prod.th7Disabled && s.prod.th8Disabled && s.prod.th9Disabled && s.prod.th10Disabled);
    }

    @computed get slidesPerView(): number {
        return getThumbnailSlidesPerView(screenResizeStore.width);
    }

    private slidesInput(): ProductSlidesInput {
        return productSlidesInput();
    }

    private clearZoomTimers() {
        clearScheduledTimeouts(this.zoomTimers);
        this.zoomTimers = [];
    }

    private clearFocusTimer() {
        if (this.focusTimer != null) {
            clearTimeout(this.focusTimer);
            this.focusTimer = null;
        }
    }

    private teardownZoom() {
        this.clearZoomTimers();
        pauseVideosIn(typeof document === 'undefined' ? null : document.getElementById(ZOOM_SWIPER_ID));
    }

    private restoreOpenerFocus() {
        const opener = this.openerEl;
        if (!opener || typeof document === 'undefined' || !document.body.contains(opener)) {
            this.openerEl = null;
            return;
        }
        this.openerEl = null;
        this.clearFocusTimer();
        this.focusTimer = setTimeout(() => {
            if (document.body.contains(opener)) {
                opener.focus({preventScroll: true});
            }
        }, 0);
    }

    @action
    private closeZoom = () => {
        this.teardownZoom();
        s.ui.zoom_thumbnails = false;
        this.restoreOpenerFocus();
    };

    @action
    loadSwiper = async (_swiper: any) => {
        ariaPulse();
    };

    @action
    loadSwiperZoom = async (swiper: any) => {
        this.clearZoomTimers();
        const index = findSlideIndexById(buildProductSlides(this.slidesInput()), this.zoom_id);
        const isVideo = typeof this.zoom_id === 'string' && this.zoom_id.startsWith('slide-prod-video-');
        if (index >= 0) {
            this.zoomTimers = scheduleZoomNavigation({
                swiper,
                index,
                isVideo,
                playVideo: () => {
                    if (swiper?.destroyed) {
                        return;
                    }
                    document
                        .querySelector<HTMLVideoElement>(`#${ZOOM_SWIPER_ID} .swiper-slide-active video`)
                        ?.play()
                        ?.catch(() => {});
                },
            });
        }
        this.zoom_id = '';
        ariaPulse();
    };

    @action
    toggleZoom = async (id: string, opener?: HTMLElement | null) => {
        this.clearZoomTimers();
        if (!id) {
            return;
        }
        this.zoom_id = id;
        this.openerEl = opener ?? (document.activeElement instanceof HTMLElement ? document.activeElement : null);
        prerender_set_pcs();

        let clone: ProductCustomizationState;

        try {
            clone = structuredClone(s.pcs);
        } catch (e) {
            clone = JSON.parse(JSON.stringify(s.pcs));
        }

        s.ps.css_zoom = r.render(clone, RenderType.Zoom);
        s.ui.zoom_thumbnails = true;
        modal(ZOOM_MODAL_ID);
    };

    render() {
        if (!s.ready) return;

        if (!this.show_slider) return;

        const slides = buildProductSlides(this.slidesInput());
        if (slides.length < 1) {
            return;
        }

        const slidesPerView = this.slidesPerView;
        const doLoop = shouldLoopThumbnails(slides.length, slidesPerView, s.prod.CustomizationMethod);
        const showNavigation = shouldShowThumbnailNavigation(slides.length, slidesPerView);

        return (
            <div>
                <Swiper
                    id={THUMB_SWIPER_ID}
                    key={`prod-swiper-th-loop-${doLoop}`}
                    onInit={this.loadSwiper}
                    slidesPerView={slidesPerView}
                    breakpoints={THUMBNAIL_SWIPER_BREAKPOINTS}
                    centeredSlidesBounds={!doLoop}
                    centeredSlides={!doLoop}
                    centerInsufficientSlides={!doLoop}
                    grabCursor={doLoop}
                    zoom={false}
                    keyboard={{
                        enabled: doLoop,
                    }}
                    scrollbar={false}
                    navigation={showNavigation}
                    height={75}
                    loop={doLoop}
                    pagination={false}
                    modules={[Keyboard, Scrollbar, Navigation, Zoom]}
                    aria-roledescription="carousel"
                    role="region"
                    aria-label="Product images"
                >
                    {slides.map(slide => (
                        <SwiperSlide
                            key={slide.id}
                            data-product-slide-id={slide.id}
                        >
                            <button
                                type="button"
                                className="prod-swiper-th-thumb"
                                aria-label={slide.accessibleName}
                                aria-haspopup="dialog"
                                onClick={e => this.toggleZoom(slide.id, e.currentTarget)}
                            >
                                {slide.thumb.showPlayOverlay ? (
                                    <div className="slide-video-thumb">
                                        <StandardSlide
                                            url={slide.thumb.url}
                                            alt={slide.thumb.alt}
                                            border={slide.thumb.border}
                                            backgroundImage={slide.thumb.backgroundImage}
                                            imgStyle={slide.thumb.imgStyle}
                                        />
                                        <span
                                            className="slide-video-play-overlay"
                                            aria-hidden="true"
                                        ></span>
                                    </div>
                                ) : (
                                    <StandardSlide
                                        url={slide.thumb.url}
                                        alt={slide.thumb.alt}
                                        border={slide.thumb.border}
                                        backgroundImage={slide.thumb.backgroundImage}
                                        imgStyle={slide.thumb.imgStyle}
                                    />
                                )}
                            </button>
                        </SwiperSlide>
                    ))}
                </Swiper>
                <SliderTHModal
                    slides={slides}
                    onSwiperInit={this.loadSwiperZoom}
                    onClose={this.closeZoom}
                />
            </div>
        );
    }
}
